import axios from 'axios';
import { notification } from 'antd';
import { REFRESH_SESSION } from '../constants/apiEndPoint';

const API_URL = process.env.REACT_APP_API_ENDPOINT;

const request = axios.create({
  baseURL: API_URL,
  timeout: 15000,
});

// Tránh gọi refresh đồng thời khi nhiều request cùng bị 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const userLogout = () => {
  notification.error({ message: 'Phiên đăng nhập hết hạn! Vui lòng đăng nhập lại.' });
  localStorage.removeItem('user');
  window.location.href = '/login';
};

const handleError = async (error) => {
  if (error?.response?.status === 400) {
    notification.error({ message: error?.response?.data?.message || 'Có lỗi xảy ra, vui lòng thử lại!' });
  }

  const originalConfig = error.config;

  // Bỏ qua các request auth (login, refresh) để tránh vòng lặp
  if (originalConfig.url.search('/auth/') !== -1 || !error.response) {
    return Promise.reject(error?.response || error);
  }

  if ([401, 402].includes(error.response.status) && !originalConfig._retry) {
    // Nếu đang refresh → đẩy request vào hàng đợi
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalConfig.headers.Authorization = `Bearer ${token}`;
          return request(originalConfig);
        })
        .catch((err) => Promise.reject(err));
    }

    originalConfig._retry = true;
    isRefreshing = true;

    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const refreshKey = user?.refresh_key;

      if (!refreshKey) {
        processQueue(new Error('No refresh_key'), null);
        userLogout();
        return Promise.reject(error?.response || error);
      }

      // Gọi refresh session — dùng axios gốc để tránh interceptor loop
      const { data } = await axios.post(`${API_URL}${REFRESH_SESSION}`, {
        refresh_key: refreshKey,
        user_id: user?.id,
      });

      if (data?.status === 1) {
        // Merge data cũ + mới → giữ fields cũ, ghi đè token + refresh_key mới
        const responseData = data?.data || data;
        const newUserData = { ...user, ...responseData };
        localStorage.setItem('user', JSON.stringify(newUserData));

        // Dispatch event để Redux store đồng bộ
        window.dispatchEvent(new CustomEvent('session-refreshed', { detail: newUserData }));

        const newToken = newUserData?.token;
        processQueue(null, newToken);

        // Retry request gốc với token mới
        originalConfig.headers.Authorization = `Bearer ${newToken}`;
        return request(originalConfig);
      } else {
        processQueue(new Error('Refresh failed'), null);
        userLogout();
      }
    } catch (refreshError) {
      console.error('Refresh session failed:', refreshError);
      processQueue(refreshError, null);
      userLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }

  return Promise.reject(error?.response || error);
};

const handleSuccess = async (res) => {
  if (res.data?.code === 404) {
    notification.error({ message: `${res.config.url} ${res.data?.message || 'API not found'}` });
  }
  return res;
};

const handleRequest = async (config) => {
  const { headers } = config;
  const requestConfig = config;
  const user = localStorage.getItem('user');
  if (user) {
    try {
      const decodeUser = JSON.parse(user);
      requestConfig.headers = { ...headers, Authorization: `Bearer ${decodeUser?.token}` };
    } catch (e) {
      localStorage.removeItem('user');
    }
  }
  return requestConfig;
};

request.interceptors.response.use(handleSuccess, handleError);
request.interceptors.request.use(handleRequest, (error) => Promise.reject(error));

export default request;
